import { type MutationOptions, type OperationVariables, type QueryOptions } from '@apollo/client/core';
import { ref } from 'vue';
import { useServices } from '@/core/composables/services';
import { type Context, type ResponseData } from '@/core/plugins/api/api';

export const useQuery = <TResult extends Record<string, unknown>, TVariables extends OperationVariables>(
  query: QueryOptions['query'],
  variables?: TVariables,
  context?: Context,
) => {
  const { $api } = useServices();

  const errors = ref<Record<string, unknown>[] | null>(null);

  const data = ref<TResult | null>(null);

  const response = ref<ResponseData<TResult> | null>(null);

  const request = async () => {
    const res = await $api.query<TResult, TVariables>(query, variables, context);

    if (res?.data) {
      data.value = res.data;
    }

    if (res?.errors) {
      errors.value = res.errors;
    }

    response.value = res;

    return res;
  };

  return { request, errors, data, response };
};

export const useMutation = <TResult extends Record<string, unknown>, TVariables extends OperationVariables>(
  mutation: MutationOptions['mutation'],
  variables: TVariables,
) => {
  const { $api } = useServices();

  const errors = ref<Record<string, unknown>[] | null>(null);

  const data = ref<TResult | null>(null);

  const response = ref<ResponseData<TResult> | null>(null);

  const request = async () => {
    const res = await $api.mutation<TResult, TVariables>(mutation, variables);

    if (res?.data) {
      data.value = res.data;
    }

    if (res?.errors) {
      errors.value = res.errors;
    }

    if (res) {
      response.value = res;
    } else {
      response.value = {
        data: null,
        errors: null,
      };
    }

    return res;
  };

  return { request, errors, data, response };
};
